import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Camera,
  X,
  Handshake,
  Package,
  Tag,
  CalendarRange,
  Wallet,
  CheckCircle2,
  Clock,
  Camera as CameraIcon,
  FileText,
  Eye,
  Trophy,
  Plus,
  ClipboardList,
  ShoppingBag,
  Sparkles,
  Building2,
  Store,
  Layers,
  ArrowLeft,
  Mail,
  Check,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

function InfoCard({
  title,
  icon: Icon,
  children,
  className,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`overflow-hidden rounded-xl border border-border bg-card shadow-sm ${className ?? ""}`}>
      <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-2.5">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-tudo-blue/10">
          <Icon className="h-3.5 w-3.5 text-tudo-blue" />
        </div>
        <h3 className="text-xs font-bold uppercase tracking-wide text-tudo-blue">
          {title}
        </h3>
      </div>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 p-4 sm:grid-cols-2">
        {children}
      </dl>
    </section>
  );
}

function Field({
  label,
  value,
  full = false,
}: {
  label: string;
  value?: string;
  full?: boolean;
}) {
  return (
    <div className={`flex flex-col gap-0.5 ${full ? "sm:col-span-2" : ""}`}>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="break-words text-sm font-medium text-foreground">
        {value && value.trim() ? value : <span className="text-muted-foreground/50">—</span>}
      </dd>
    </div>
  );
}

const accentMap = {
  green: {
    bar: "bg-tudo-green",
    soft: "bg-tudo-green/10 text-tudo-green",
    dot: "bg-tudo-green",
    text: "text-tudo-green",
  },
  red: {
    bar: "bg-tudo-red",
    soft: "bg-tudo-red/10 text-tudo-red",
    dot: "bg-tudo-red",
    text: "text-tudo-red",
  },
  orange: {
    bar: "bg-tudo-orange",
    soft: "bg-tudo-orange/10 text-tudo-orange",
    dot: "bg-tudo-orange",
    text: "text-tudo-orange",
  },
  blue: {
    bar: "bg-tudo-blue",
    soft: "bg-tudo-blue/10 text-tudo-blue",
    dot: "bg-tudo-blue",
    text: "text-tudo-blue",
  },
} as const;

type Accent = keyof typeof accentMap;

function SectionBlock({
  title,
  icon: Icon,
  accent,
  count,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: Accent;
  count?: number;
  children: React.ReactNode;
}) {
  const a = accentMap[accent];
  return (
    <section className="px-4 py-4 sm:px-5">
      <div className="mb-3 flex items-center gap-2.5">
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${a.soft}`}>
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="flex-1 text-sm font-bold uppercase tracking-wide text-foreground">
          {title}
        </h3>
        {typeof count === "number" && (
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${a.soft}`}>
            {count}
          </span>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function InvestmentCard({
  item,
  accent,
  actions,
}: {
  item: {
    product: string;
    type: string;
    subtype: string;
    value: string;
    period: string;
    status: string;
  };
  accent: Accent;
  actions: React.ReactNode;
}) {
  const a = accentMap[accent];
  return (
    <div className="group relative overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-all hover:border-tudo-blue/30 hover:shadow-md">
      <div className={`absolute inset-y-0 left-0 w-1 ${a.bar}`} />
      <div className="grid grid-cols-1 gap-4 p-4 pl-5 md:grid-cols-[1.4fr_1fr_auto] md:items-center">
        <div className="min-w-0 space-y-1.5">
          <div className="flex items-start gap-2">
            <Package className="mt-0.5 h-4 w-4 shrink-0 text-tudo-blue/70" />
            <p className="text-sm font-bold leading-snug text-foreground">{item.product}</p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 pl-6">
            <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              <Tag className="h-3 w-3" />
              {item.type}
            </span>
            <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {item.subtype}
            </span>
          </div>
        </div>

        <div className="space-y-1.5 md:border-l md:border-border md:pl-4">
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 shrink-0 text-tudo-blue/70" />
            <span className="text-base font-bold text-tudo-blue">{item.value}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CalendarRange className="h-3.5 w-3.5 shrink-0" />
            {item.period}
          </div>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${a.soft}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${a.dot}`} />
            {item.status}
          </span>
        </div>

        <div className="flex flex-col gap-2 md:w-36">{actions}</div>
      </div>
    </div>
  );
}

export interface NegotiationsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSolicitarInvestimento?: () => void;
}


const availableInvestments = [
  {
    product: "Dorflex",
    type: "Compra de Espaço",
    subtype: "BALCÃO OTC",
    value: "R$ 200,00",
    period: "De 01/07/2019 - 31/07/2019",
    status: "Elegível",
  },
  {
    product: "Dulcolax, Allegra, Enterogermina",
    type: "Compra de Espaço",
    subtype: "Display Balcão da Loja",
    value: "R$ 600,00",
    period: "De 01/07/2019 - 31/07/2019",
    status: "Elegível",
  },
  {
    product: "Lantus, Toujeo, Apidra",
    type: "Compra de Espaço",
    subtype: "Móvel Ponta",
    value: "R$ 600,00",
    period: "De 01/07/2019 - 31/07/2019",
    status: "Elegível",
  },
];

type Investment = (typeof availableInvestments)[number];

const reasons = [
  "Objetivo de demanda não aceito pelo cliente.",
  "Valor do investimento abaixo do praticado pelo PDV.",
  "PDV não possui os produtos expostos fora do balcão.",
  "Cliente sem crédito.",
  "Cliente já comprometido com concorrentes.",
  "Cliente não quer o investimento pois demora a receber.",
  "Outros / justifique:",
];

const auditTypes = [
  "Ativação não vigente.",
  "Gerente não autorizou acao.",
  "Loja em reforma.",
  "Loja não possui espaço negociado.",
  "Loja não recebeu comunicado da acao.",
  "produto atrás do balcão.",
  "Rebaixa de Preço não vigente.",
  "VB apenas a ponto natural.",
];

const proofItems = [
  {
    product: "Dorflex",
    type: "Compra de Espaço",
    subtype: "BALCÃO OTC",
    value: "R$ 200,00",
    period: "De 01/07/2019 - 31/07/2019",
    status: "Negociado",
  },
  {
    product: "Dulcolax, Allegra, Enterogermina",
    type: "Compra de Espaço",
    subtype: "Display Balcão da Loja",
    value: "R$ 600,00",
    period: "De 01/07/2019 - 31/07/2019",
    status: "Negociado",
  },
  {
    product: "Lantus, Toujeo, Apidra",
    type: "Compra de Espaço",
    subtype: "Móvel Ponta",
    value: "R$ 600,00",
    period: "De 01/07/2019 - 31/07/2019",
    status: "Negociado",
  },
];

const pendingItems = [
  {
    product: "DORFLEX, NOVALGINA, DULCOLAX, ALLEGRA, ENTEROGERMINA, BISOLVON, LANTUS, APIDRA",
    type: "Compra de Espaço",
    subtype: "ARMÁRIO GENÉRICOS",
    value: "R$ 200,00",
    period: "De 01/07/2019 - 31/07/2019",
    status: "Pendente",
  },
  {
    product: "DORFLEX, NOVALGINA, DULCOLAX, ALLEGRA, ENTEROGERMINA, BISOLVON, LANTUS, APIDRA",
    type: "Compra de Espaço",
    subtype: "ARMÁRIO DERMATOLÓGICOS",
    value: "R$ 200,00",
    period: "De 01/07/2019 - 31/07/2019",
    status: "Pendente",
  },
  {
    product: "NOVALGINA",
    type: "Compra de Espaço",
    subtype: "BALCÃO OTC",
    value: "R$ 200,00",
    period: "De 01/07/2019 - 31/07/2019",
    status: "Pendente",
  },
];

const provenItems = [
  {
    product: "DORFLEX, NOVALGINA, DULCOLAX, ALLEGRA, ENTEROGERMINA, BISOLVON, LANTUS, APIDRA",
    type: "Compra de Espaço",
    subtype: "ARMÁRIO GENÉRICOS",
    value: "R$ 200,00",
    period: "De 01/07/2019 - 31/07/2019",
    status: "Comprovado",
  },
];

const roadmapItems = [
  {
    name: "Brinde",
    description: "Distribuição de brindes de relacionamento aos clientes do PDV.",
    objetivo: "Fortalecer o relacionamento e estimular a recompra.",
    steps: [
      "Conferir o estoque de brindes disponíveis no PDV.",
      "Posicionar os brindes próximos ao balcão de atendimento.",
      "Orientar a equipe sobre o critério de entrega ao cliente.",
      "Registrar a entrega no sistema a cada brinde distribuído.",
    ],
  },
  {
    name: "Campanha de vendas",
    description: "Ação promocional com metas de venda para os produtos participantes.",
    objetivo: "Aumentar o giro e a positivação dos itens da campanha.",
    steps: [
      "Validar os produtos e o período de vigência da campanha.",
      "Garantir exposição destacada dos itens participantes.",
      "Comunicar a equipe sobre as metas e mecânicas da campanha.",
      "Acompanhar diariamente o desempenho das vendas.",
    ],
  },
  {
    name: "Armário genéricos",
    description: "Organização e exposição do armário de medicamentos genéricos.",
    objetivo: "Padronizar a exposição e facilitar a localização pelo consumidor.",
    steps: [
      "Limpar e organizar o armário conforme o planograma.",
      "Posicionar os genéricos por ordem alfabética ou categoria.",
      "Conferir a precificação e etiquetas de todos os itens.",
      "Registrar foto do armário após a organização.",
    ],
  },
];

type RoadmapItem = (typeof roadmapItems)[number];

const reportItems = [
  { name: "Balcão Bandeja da Marca" },
  { name: "Balcão Display" },
  { name: "Balcão Faixa de Gôndola" },
  { name: "Brinde de relacionamento" },
  { name: "Cesto Promocional" },
];

type ScopeType = "unidade" | "categoria" | "marca";

const scopeConfig: Record<
  ScopeType,
  { title: string; subtitle: string; options: string[] }
> = {
  unidade: {
    title: "Unidade de Negócio",
    subtitle: "Selecione a unidade de negócio",
    options: ["DERMATOLOGIA", "GENÉRICOS", "MIP", "PRESCRIÇÃO", "INST E CUIDADOS ESPECIAIS"],
  },
  categoria: {
    title: "Categoria",
    subtitle: "Selecione a categoria do produto",
    options: ["Analgésicos", "Antialérgicos", "Antibióticos", "Dermocosméticos", "Gastrointestinais", "Vitaminas"],
  },
  marca: {
    title: "Marca",
    subtitle: "Selecione a marca",
    options: ["Dorflex", "Novalgina", "Dulcolax", "Allegra", "Enterogermina", "Lantus", "Apidra"],
  },
};



export function NegotiationsModal({ open, onOpenChange, onSolicitarInvestimento }: NegotiationsModalProps) {
  const [infoOpen, setInfoOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [proofOpen, setProofOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditTypeOpen, setAuditTypeOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [termOpen, setTermOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [roteiroOpen, setRoteiroOpen] = useState(false);
  const [selectedRoteiro, setSelectedRoteiro] = useState<RoadmapItem | null>(null);
  const [scopeModal, setScopeModal] = useState<{ idx: number; type: ScopeType } | null>(null);
  const [scopeSelections, setScopeSelections] = useState<Record<string, string>>({});
  const scopeGuardRef = useRef(false);
  const prevScopeOpenRef = useRef(false);

  useEffect(() => {
    const scopeOpen = scopeModal !== null;
    if (prevScopeOpenRef.current && !scopeOpen) {
      scopeGuardRef.current = true;
      const t = window.setTimeout(() => {
        scopeGuardRef.current = false;
      }, 350);
      prevScopeOpenRef.current = scopeOpen;
      return () => window.clearTimeout(t);
    }
    prevScopeOpenRef.current = scopeOpen;
  }, [scopeModal]);
  const [termEmail, setTermEmail] = useState("");
  const [selectedAuditType, setSelectedAuditType] = useState<string | null>(null);
  const [auditObservation, setAuditObservation] = useState("");
  const [selectedInvestment, setSelectedInvestment] = useState<Investment | null>(null);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [otherReason, setOtherReason] = useState("");
  const [beforePhoto, setBeforePhoto] = useState<string | null>(null);
  const [afterPhoto, setAfterPhoto] = useState<string | null>(null);
  const [reportPhotos, setReportPhotos] = useState<Record<number, string | null>>({});
  const reportInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const beforeInputRef = useRef<HTMLInputElement>(null);
  const afterInputRef = useRef<HTMLInputElement>(null);

  const childOpen =
    infoOpen ||
    detailOpen ||
    proofOpen ||
    auditOpen ||
    auditTypeOpen ||
    reportOpen ||
    termOpen ||
    receiptOpen ||
    roteiroOpen ||
    scopeModal !== null;
  const guardCloseRef = useRef(false);
  const prevChildOpenRef = useRef(false);

  useEffect(() => {
    if (prevChildOpenRef.current && !childOpen) {
      guardCloseRef.current = true;
      const t = window.setTimeout(() => {
        guardCloseRef.current = false;
      }, 350);
      prevChildOpenRef.current = childOpen;
      return () => window.clearTimeout(t);
    }
    prevChildOpenRef.current = childOpen;
  }, [childOpen]);




  const handleOpenReasons = () => {
    setInfoOpen(true);
  };

  const handleOpenDetail = (investment: Investment) => {
    setSelectedInvestment(investment);
    setDetailOpen(true);
  };

  const handleOpenProof = () => {
    setDetailOpen(false);
    setProofOpen(true);
  };

  const handleOpenTerm = () => {
    setTermOpen(true);
  };

  const handleOpenReceipt = () => {
    setReceiptOpen(true);
  };

  const handleOpenAudit = () => {
    setProofOpen(false);
    setAuditOpen(true);
  };

  const handleOpenAuditType = () => {
    setAuditOpen(false);
    setAuditTypeOpen(true);
  };

  const handlePhotoCapture = (e: React.ChangeEvent<HTMLInputElement>, setter: (value: string | null) => void) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setter(url);
    }
  };

  const handleReportPhotoCapture = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setReportPhotos((prev) => ({ ...prev, [idx]: url }));
    }
  };


  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          onInteractOutside={(e) => {
            if (childOpen || guardCloseRef.current) e.preventDefault();
          }}
          onFocusOutside={(e) => {
            if (childOpen || guardCloseRef.current) e.preventDefault();
          }}
          className="flex h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden"
        >
          {/* Header */}
          <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
            <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
            <div className="relative flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                  <Handshake className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                    Negociações
                  </DialogTitle>
                  <p className="truncate text-xs font-medium text-white/70">
                    Gestão de investimentos no PDV
                  </p>
                </div>
              </div>
              <button
                onClick={() => onOpenChange(false)}
                className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 divide-y divide-border overflow-y-auto bg-muted/30">
            <SectionBlock
              title="Disponíveis para negociação"
              icon={Sparkles}
              accent="green"
              count={availableInvestments.length}
            >
              {availableInvestments.map((inv, idx) => (
                <InvestmentCard
                  key={idx}
                  item={inv}
                  accent="green"
                  actions={
                    <>
                      <button
                        onClick={() => handleOpenDetail(inv)}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-tudo-green px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-tudo-green/90"
                      >
                        <Handshake className="h-4 w-4" />
                        Negociar
                      </button>
                      <button
                        onClick={() => handleOpenReasons()}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-tudo-red/30 bg-tudo-red/5 px-4 py-2 text-sm font-semibold text-tudo-red transition-all hover:bg-tudo-red hover:text-white"
                      >
                        <X className="h-4 w-4" />
                        Recusar
                      </button>
                    </>
                  }
                />
              ))}
            </SectionBlock>

            <SectionBlock
              title="Pendente"
              icon={Clock}
              accent="red"
              count={pendingItems.length}
            >
              {pendingItems.map((item, idx) => (
                <InvestmentCard
                  key={idx}
                  item={item}
                  accent="red"
                  actions={
                    <button
                      onClick={() => handleOpenTerm()}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-all hover:border-tudo-blue hover:text-tudo-blue"
                    >
                      <FileText className="h-4 w-4" />
                      Termo
                    </button>
                  }
                />
              ))}
            </SectionBlock>

            <SectionBlock
              title="Exposição / Comprovação"
              icon={CameraIcon}
              accent="orange"
              count={proofItems.length}
            >
              {proofItems.map((item, idx) => (
                <InvestmentCard
                  key={idx}
                  item={item}
                  accent="orange"
                  actions={
                    <button
                      onClick={() => handleOpenAudit()}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-tudo-blue px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-tudo-blue/90"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Comprovar
                    </button>
                  }
                />
              ))}
            </SectionBlock>

            <SectionBlock
              title="Investimentos comprovados"
              icon={CheckCircle2}
              accent="green"
              count={provenItems.length}
            >
              {provenItems.map((item, idx) => (
                <InvestmentCard
                  key={idx}
                  item={item}
                  accent="green"
                  actions={
                    <button
                      onClick={() => handleOpenReceipt()}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-all hover:border-tudo-blue hover:text-tudo-blue"
                    >
                      <Eye className="h-4 w-4" />
                      Visualizar
                    </button>
                  }
                />
              ))}
            </SectionBlock>

            <SectionBlock
              title="Roteiro promocional"
              icon={ClipboardList}
              accent="blue"
              count={roadmapItems.length}
            >
              {roadmapItems.map((item, idx) => (
                <div
                  key={idx}
                  className="group relative flex items-center gap-3 overflow-hidden rounded-xl border border-border bg-card p-4 pl-5 shadow-sm transition-all hover:border-tudo-blue/30 hover:shadow-md"
                >
                  <div className="absolute inset-y-0 left-0 w-1 bg-tudo-blue" />
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-tudo-blue/10">
                    <ShoppingBag className="h-4 w-4 text-tudo-blue" />
                  </span>
                  <span className="flex-1 text-sm font-semibold text-foreground">{item.name}</span>
                  <button
                    onClick={() => {
                      setSelectedRoteiro(item);
                      setRoteiroOpen(true);
                    }}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-all hover:border-tudo-blue hover:text-tudo-blue"
                  >
                    <Eye className="h-4 w-4" />
                    Visualizar
                  </button>
                </div>
              ))}
            </SectionBlock>
          </div>

          {/* Footer */}
          <div className="flex shrink-0 flex-col gap-3 border-t border-border bg-card px-4 py-3 sm:flex-row sm:justify-end sm:px-5">
            <button
              onClick={() => setReportOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-tudo-blue/30 bg-tudo-blue/5 px-5 py-2.5 text-sm font-semibold text-tudo-blue transition-all hover:bg-tudo-blue hover:text-white"
            >
              <Trophy className="h-4 w-4" />
              Reportar conquista
            </button>
            <button
              onClick={() => onSolicitarInvestimento?.()}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-tudo-green to-tudo-green/85 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-tudo-green/20 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <Plus className="h-4 w-4" />
              Solicitar novo investimento
            </button>
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden">
          {/* Header */}
          <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
            <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
            <div className="relative flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                  <Handshake className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                    Detalhe do investimento
                  </DialogTitle>
                  <p className="truncate text-xs font-medium text-white/70">
                    {selectedInvestment?.product || "Novalgina"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setDetailOpen(false)}
                className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-muted/30 p-4 sm:p-5">
            <InfoCard title="Detalhe do investimento" icon={Layers}>
              <Field label="CNPJ Matriz" value="19.105.575/0001-05" />
              <Field label="Unidade de negócios" value="GENÉRICOS" />
              <Field label="Tipo" value="Investimento promocional" />
              <Field label="Sub-Tipo" value="Campanha mega venda Novalgina" />
              <Field label="Natureza" value="Produto" />
              <Field label="Produto" value={selectedInvestment?.product || "Novalgina"} />
              <Field label="Vigência de" value="18/06/2026" />
              <Field label="Vigência até" value="18/07/2026" />
            </InfoCard>

            <InfoCard title="Contrapartida do investimento" icon={Handshake}>
              <Field label="Demanda acordada" value="20 unidades" />
              <Field label="Prazo" value="2 meses" />
              <div className="flex flex-col gap-1 sm:col-span-2">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Demais contrapartidas
                </dt>
                <dd>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-tudo-green/10 px-3 py-1 text-sm font-semibold text-tudo-green">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    7 unidades por semana
                  </span>
                </dd>
              </div>
            </InfoCard>
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-card px-4 py-3 sm:px-5">
            <button
              onClick={() => setDetailOpen(false)}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:border-tudo-blue hover:text-tudo-blue"
            >
              <X className="h-4 w-4" />
              Cancelar
            </button>
            <button
              onClick={() => setDetailOpen(false)}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-tudo-green to-tudo-green/85 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-tudo-green/20 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <CheckCircle2 className="h-4 w-4" />
              Concluir
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={proofOpen} onOpenChange={setProofOpen}>
        <DialogContent className="w-full max-w-5xl overflow-hidden rounded-xl border-none p-0 shadow-2xl [&>button]:hidden">
          <div className="relative flex items-center justify-between bg-tudo-orange px-5 py-3">
            <DialogTitle className="text-left text-sm font-bold uppercase tracking-wide text-white">
              Exposição/Comprovação
            </DialogTitle>
            <button
              onClick={() => setProofOpen(false)}
              className="text-white/80 transition-colors hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto bg-gray-50 p-5">
            <div className="space-y-4">
              {proofItems.map((item, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-1 items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_1fr_auto]"
                >
                  <div className="space-y-1 text-sm text-foreground">
                    <p>
                      <span className="font-semibold">Produto:</span> {item.product}
                    </p>
                    <p>
                      <span className="font-semibold">Tipo:</span> {item.type}
                    </p>
                    <p>
                      <span className="font-semibold">Subtipo:</span> {item.subtype}
                    </p>
                  </div>

                  <div className="space-y-1 text-sm text-foreground">
                    <p>
                      <span className="font-semibold">Valor:</span> {item.value}
                    </p>
                    <p>
                      <span className="font-semibold">Vigência:</span> {item.period}
                    </p>
                    <p>
                      <span className="font-semibold">Status:</span>{" "}
                      <span className="font-semibold text-tudo-orange">{item.status}</span>
                    </p>
                  </div>

                  <button
                    onClick={() => handleOpenAudit()}
                    className="min-w-[8rem] rounded-md bg-tudo-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-tudo-blue/80"
                  >
                    Comprovar
                  </button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden">
          {/* Header */}
          <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
            <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
            <div className="relative flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                  <CameraIcon className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                    Exposição / Comprovação
                  </DialogTitle>
                  <p className="truncate text-xs font-medium text-white/80">
                    Dorflex, Dulcolax, Allegra, Enterogermina, Lantus, Toujeo, Apidra
                  </p>
                </div>
              </div>
              <button
                onClick={() => setAuditOpen(false)}
                className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-muted/30 p-4 sm:p-5">
            <InfoCard title="Dados do investimento" icon={Layers}>
              <Field label="Vigência" value="07/19 - 07/19" />
              <Field label="Bandeira" value="I" />
              <Field label="CNPJ" value="19.105.575/0001-05" />
              <Field label="Natureza" value="Valor" />
              <Field label="Esfera de negociação" value="UNIDADE DE NEGOCIO" />
              <Field label="Unidade de negócios" value="DERMATOLOGIA" />
              <Field label="Tipo de investimento" value="Compra de Espaço" />
              <Field label="Sub tipo de investimento" value="Cestao" />
              <Field label="Grupo de produtos" value="DORFLEX, DULCOLAX, ALLEGRA, ENTEROGERMINA, LANTUS, TOUJEO, APIDRA" full />
              <Field label="Valor de retorno" value="R$ 200,00" />
            </InfoCard>

            <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
              <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-2.5">
                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-tudo-blue/10">
                  <CameraIcon className="h-3.5 w-3.5 text-tudo-blue" />
                </div>
                <h3 className="text-xs font-bold uppercase tracking-wide text-tudo-blue">
                  Evidências fotográficas
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                {[
                  { label: "Foto Antes", photo: beforePhoto, ref: beforeInputRef, setter: setBeforePhoto },
                  { label: "Foto Depois", photo: afterPhoto, ref: afterInputRef, setter: setAfterPhoto },
                ].map((slot) => (
                  <div key={slot.label} className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {slot.label}
                    </p>
                    <input
                      ref={slot.ref}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handlePhotoCapture(e, slot.setter)}
                    />
                    {slot.photo ? (
                      <div className="group relative overflow-hidden rounded-xl border border-border">
                        <img
                          src={slot.photo}
                          alt={slot.label}
                          className="h-40 w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => slot.ref.current?.click()}
                          className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 text-sm font-semibold text-transparent transition-all hover:bg-black/50 hover:text-white"
                        >
                          <Camera className="h-4 w-4" />
                          Substituir
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => slot.ref.current?.click()}
                        className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/30 text-sm font-semibold text-muted-foreground transition-all hover:border-tudo-blue hover:bg-tudo-blue/5 hover:text-tudo-blue"
                      >
                        <Camera className="h-6 w-6" />
                        Tirar foto
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="flex shrink-0 flex-col gap-3 border-t border-border bg-card px-4 py-3 sm:flex-row sm:justify-end sm:px-5">
            <button
              onClick={() => handleOpenAuditType()}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-tudo-blue/30 bg-tudo-blue/5 px-5 py-2.5 text-sm font-semibold text-tudo-blue transition-all hover:bg-tudo-blue hover:text-white"
            >
              <ClipboardList className="h-4 w-4" />
              Reportar Auditoria
            </button>
            <button
              onClick={() => setAuditOpen(false)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-tudo-green to-tudo-green/85 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-tudo-green/20 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <CheckCircle2 className="h-4 w-4" />
              Confirmar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="w-full max-w-2xl overflow-hidden rounded-xl border-none p-0 shadow-2xl [&>button]:hidden">
          <div className="relative flex items-center justify-between bg-tudo-blue px-5 py-3">
            <DialogTitle className="text-left text-sm font-bold uppercase tracking-wide text-white">
              Investimentos
            </DialogTitle>
            <button
              onClick={() => setInfoOpen(false)}
              className="text-white/80 transition-colors hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="bg-gray-200 px-5 py-2 text-sm font-semibold text-gray-800">Motivos</div>

          <div className="max-h-[70vh] overflow-y-auto p-5">
            <div className="space-y-3">
              {reasons.map((reason) => (
                <label
                  key={reason}
                  className={`flex cursor-pointer items-start gap-3 rounded-md border px-4 py-3 text-sm transition-colors ${selectedReason === reason
                      ? "border-tudo-blue bg-tudo-blue/5"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}
                >
                  <span
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${selectedReason === reason ? "border-tudo-blue" : "border-gray-300"
                      }`}
                  >
                    {selectedReason === reason && <span className="h-2.5 w-2.5 rounded-full bg-tudo-blue" />}
                  </span>
                  <span className="text-foreground">{reason}</span>
                  <input
                    type="radio"
                    name="reason"
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={() => setSelectedReason(reason)}
                    className="sr-only"
                  />
                </label>
              ))}
            </div>

            {selectedReason === "Outros / justifique:" && (
              <div className="mt-3">
                <textarea
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  placeholder="Informe o motivo..."
                  rows={3}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-foreground placeholder:text-gray-400 focus:border-tudo-blue focus:outline-none focus:ring-1 focus:ring-tudo-blue"
                />
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setInfoOpen(false)}
                className="rounded-md border border-gray-200 bg-white px-6 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50"
              >
                Voltar
              </button>
              <button
                onClick={() => setInfoOpen(false)}
                className="rounded-md bg-tudo-blue px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-tudo-blue/80"
              >
                Confirmar
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={auditTypeOpen} onOpenChange={setAuditTypeOpen}>
        <DialogContent className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden">
          {/* Header */}
          <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
            <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
            <div className="relative flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                  <ClipboardList className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                    Tipo de Auditoria
                  </DialogTitle>
                  <p className="truncate text-xs font-medium text-white/70">
                    Selecione o motivo da não conformidade
                  </p>
                </div>
              </div>
              <button
                onClick={() => setAuditTypeOpen(false)}
                className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-muted/30 p-4 sm:p-5">
            <div className="space-y-2">
              {auditTypes.map((type) => {
                const active = selectedAuditType === type;
                return (
                  <label
                    key={type}
                    className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 text-sm transition-all ${active
                        ? "border-tudo-blue bg-tudo-blue/5 shadow-sm"
                        : "border-border bg-card hover:border-tudo-blue/40 hover:bg-tudo-blue/5"
                      }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${active ? "border-tudo-blue bg-tudo-blue" : "border-border"
                        }`}
                    >
                      {active && <CheckCircle2 className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                    </span>
                    <span className={`flex-1 ${active ? "font-semibold text-tudo-blue" : "text-foreground"}`}>
                      {type}
                    </span>
                    <input
                      type="radio"
                      name="auditType"
                      value={type}
                      checked={active}
                      onChange={() => setSelectedAuditType(type)}
                      className="sr-only"
                    />
                  </label>
                );
              })}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Observação
              </label>
              <textarea
                value={auditObservation}
                onChange={(e) => setAuditObservation(e.target.value)}
                placeholder="Informe uma observação..."
                rows={3}
                className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-tudo-blue focus:outline-none focus:ring-2 focus:ring-tudo-blue/30"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-card px-4 py-3 sm:px-5">
            <button
              onClick={() => setAuditTypeOpen(false)}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:border-tudo-blue hover:text-tudo-blue"
            >
              <X className="h-4 w-4" />
              Cancelar
            </button>
            <button
              onClick={() => setAuditTypeOpen(false)}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-tudo-green to-tudo-green/85 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-tudo-green/20 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <CheckCircle2 className="h-4 w-4" />
              Confirmar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent
          onInteractOutside={(e) => {
            if (scopeModal !== null || scopeGuardRef.current) e.preventDefault();
          }}
          onFocusOutside={(e) => {
            if (scopeModal !== null || scopeGuardRef.current) e.preventDefault();
          }}
          className="flex h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden"
        >
          {/* Header */}
          <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
            <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
            <div className="relative flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                  <Trophy className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                    Reportar Conquista
                  </DialogTitle>
                  <p className="truncate text-xs font-medium text-white/70">
                    Classifique e anexe a evidência de cada conquista
                  </p>
                </div>
              </div>
              <button
                onClick={() => setReportOpen(false)}
                className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-muted/30 p-4 sm:p-5">
            {reportItems.map((item, idx) => {
              const scopes: { type: ScopeType; label: string }[] = [
                { type: "unidade", label: "Unidade de Negócio" },
                { type: "categoria", label: "Categoria" },
                { type: "marca", label: "Marca" },
              ];
              return (
                <div
                  key={idx}
                  className="overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-tudo-blue/10">
                      <Trophy className="h-4 w-4 text-tudo-blue" />
                    </div>
                    <span className="text-sm font-bold text-foreground">{item.name}</span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {scopes.map((scope) => {
                      const value = scopeSelections[`${idx}-${scope.type}`];
                      return (
                        <button
                          key={scope.type}
                          type="button"
                          onClick={() => setScopeModal({ idx, type: scope.type })}
                          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-all ${value
                              ? "border-tudo-blue bg-tudo-blue/5 text-tudo-blue"
                              : "border-border bg-card text-muted-foreground hover:border-tudo-blue/40 hover:text-tudo-blue"
                            }`}
                        >
                          {value ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                          {value ?? scope.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-3">
                    <input
                      ref={(el) => { reportInputRefs.current[idx] = el; }}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handleReportPhotoCapture(e, idx)}
                    />
                    {reportPhotos[idx] ? (
                      <div className="overflow-hidden rounded-xl border border-border">
                        <img
                          src={reportPhotos[idx]!}
                          alt={`Foto ${item.name}`}
                          className="h-36 w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => reportInputRefs.current[idx]?.click()}
                          className="flex w-full items-center justify-center gap-2 bg-muted/50 px-4 py-2 text-sm font-medium text-tudo-blue transition-colors hover:bg-muted"
                        >
                          <Camera className="h-4 w-4" />
                          Trocar imagem
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => reportInputRefs.current[idx]?.click()}
                        className="flex w-full flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-border bg-muted/30 py-5 text-sm font-medium text-muted-foreground transition-all hover:border-tudo-blue hover:bg-tudo-blue/5 hover:text-tudo-blue"
                      >
                        <Camera className="h-6 w-6" />
                        Anexar evidência
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-card px-4 py-3 sm:px-5">
            <button
              onClick={() => setReportOpen(false)}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:border-tudo-blue hover:text-tudo-blue"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </button>
            <button
              onClick={() => setReportOpen(false)}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-tudo-green to-tudo-green/85 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-tudo-green/20 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <CheckCircle2 className="h-4 w-4" />
              Confirmar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={scopeModal !== null} onOpenChange={(o) => !o && setScopeModal(null)}>
        <DialogContent className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden">
          {scopeModal && (
            <>
              {/* Header */}
              <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
                <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
                <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
                <div className="relative flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                      <Layers className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                        {scopeConfig[scopeModal.type].title}
                      </DialogTitle>
                      <p className="truncate text-xs font-medium text-white/70">
                        {scopeConfig[scopeModal.type].subtitle}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setScopeModal(null)}
                    className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                    aria-label="Fechar"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-muted/30 p-4 sm:p-5">
                {scopeConfig[scopeModal.type].options.map((opt) => {
                  const key = `${scopeModal.idx}-${scopeModal.type}`;
                  const active = scopeSelections[key] === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        setScopeSelections((prev) => ({ ...prev, [key]: opt }));
                        setScopeModal(null);
                      }}
                      className={`flex w-full items-center gap-3 rounded-lg border px-3 py-3 text-left text-sm font-medium transition-all ${active
                          ? "border-tudo-blue bg-tudo-blue/5 text-tudo-blue shadow-sm"
                          : "border-border bg-card text-foreground hover:border-tudo-blue/40 hover:bg-tudo-blue/5"
                        }`}
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${active ? "border-tudo-blue bg-tudo-blue" : "border-border"
                          }`}
                      >
                        {active && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                      </span>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={termOpen} onOpenChange={setTermOpen}>
        <DialogContent className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden">
          {/* Header */}
          <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
            <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
            <div className="relative flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                    Termo de Investimento
                  </DialogTitle>
                  <p className="truncate text-xs font-medium text-white/70">
                    Código 210139 • Compra de Espaço
                  </p>
                </div>
              </div>
              <button
                onClick={() => setTermOpen(false)}
                className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-muted/30 p-4 sm:p-5">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <InfoCard title="Empresa Investidora" icon={Building2}>
                <Field label="Empresa" value="Ache Laboratórios Farmacêuticos S/A" full />
                <Field label="Endereço" value="Av. das Nações Unidas, 22.428" full />
                <Field label="Bairro" value="Vila Almeida" />
                <Field label="Cidade" value="São Paulo" />
                <Field label="UF" value="SP" />
                <Field label="CEP" value="04795-000" />
                <Field label="CNPJ / MF" value="53.162.095/0001-06" />
                <Field label="Inscrição Estadual" value="116452148119" />
              </InfoCard>

              <InfoCard title="Empresa Investida" icon={Store}>
                <Field label="Razão Social" value="BF FARMA" full />
                <Field label="Nome Fantasia" value="BF FARMA" />
                <Field label="Endereço" value="SIQUEIRA CAMPOS, 560" />
                <Field label="Bairro" value="PRADO" />
                <Field label="Cidade" value="MACEIO" />
                <Field label="UF" value="AL" />
                <Field label="CEP" value="57010-002" />
                <Field label="CNPJ / MF" value="19.105.575/0001-05" />
                <Field label="Inscrição Estadual" value="" />
                <Field label="Responsável" value="" />
                <div className="flex flex-col gap-1 sm:col-span-2">
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <Mail className="h-3.5 w-3.5" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={termEmail}
                    onChange={(e) => setTermEmail(e.target.value)}
                    placeholder="Informe o email..."
                    className="h-9 w-full rounded-lg border border-border px-3 text-sm text-foreground transition-colors focus:border-tudo-blue focus:outline-none focus:ring-2 focus:ring-tudo-blue/30"
                  />
                </div>
              </InfoCard>
            </div>

            <InfoCard title="Dados do Investimento" icon={Layers}>
              <Field label="Código do Investimento" value="210139" />
              <Field label="Solicitante" value="BF FARMA" />
              <Field label="Tipo de Investimento" value="Compra de Espaço" />
              <Field label="Subtipo de Investimento" value="ARMÁRIO GENÉRICOS" />
              <Field label="Vigência do Investimento" value="07/19 - 07/19" />
              <Field label="Valor / % / Qtd Acordado (a)" value="R$ 200,00" />
              <Field label="Natureza" value="Valor" />
              <Field label="Esfera" value="UNIDADE DE NEGOCIO" />
              <Field label="Descrição" value="" />
              <Field label="CNPJ(s) Participantes" value="19.105.575/0001-05" />
            </InfoCard>

            <InfoCard title="Contrapartida(s) Acordada(s)" icon={Handshake}>
              <Field label="Demanda acordada" value="20 unidades" />
              <Field label="Prazo" value="2 meses" />
            </InfoCard>
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-card px-4 py-3 sm:px-5">
            <button
              onClick={() => setTermOpen(false)}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:border-tudo-blue hover:text-tudo-blue"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </button>
            <button
              onClick={() => setTermOpen(false)}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-tudo-green to-tudo-green/85 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-tudo-green/20 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <CheckCircle2 className="h-4 w-4" />
              Aceitar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="w-full max-w-2xl overflow-hidden rounded-xl border-none p-0 shadow-2xl [&>button]:hidden">
          <div className="relative flex items-center justify-between bg-tudo-blue px-5 py-3">
            <DialogTitle className="text-left text-sm font-bold uppercase tracking-wide text-white">
              Recibo
            </DialogTitle>
            <button
              onClick={() => setReceiptOpen(false)}
              className="text-white/80 transition-colors hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="max-h-[80vh] overflow-y-auto p-8 text-center">
            <h2 className="text-3xl font-semibold uppercase tracking-wide text-tudo-blue">Recibo</h2>

            <div className="mt-8 space-y-6 text-sm text-left text-foreground">
              <p>
                Recebemos do <span className="font-semibold">TudoFarma Serviços de Tecnologia Ltda</span>,{" "}
                <span className="font-semibold">21.565.281/0001-08</span>, a importância de{" "}
                <span className="font-semibold">R$ 1,63</span> /{" "}
                <span className="font-semibold uppercase">UM REAL E SESSENTA E TRÊS CENTAVOS</span>, referente a , para{" "}
                <span className="font-semibold">GENERICOS</span> no período de{" "}
                <span className="font-semibold">01/11/2018 a 30/11/2018</span>.
              </p>

              <p>
                <span className="font-semibold">CNPJ:</span> 12.105.236/0001-71
              </p>

              <p className="font-semibold">PETROPOLIS, 18 de junho de 2026</p>

              <p className="text-xs text-gray-500">
                *Este recibo é válido somente mediante depósito bancário.
              </p>
            </div>
          </div>

          <div className="flex shrink-0 justify-end border-t border-gray-200 bg-gray-50 px-5 py-4">
            <button
              onClick={() => setReceiptOpen(false)}
              className="min-w-[8rem] rounded-md bg-tudo-blue px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-tudo-blue/80"
            >
              Voltar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={roteiroOpen} onOpenChange={setRoteiroOpen}>
        <DialogContent className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden">
          {/* Header */}
          <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
            <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
            <div className="relative flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                  <ClipboardList className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                    {selectedRoteiro?.name ?? "Roteiro promocional"}
                  </DialogTitle>
                  <p className="truncate text-xs font-medium text-white/70">
                    Roteiro promocional • Execução no PDV
                  </p>
                </div>
              </div>
              <button
                onClick={() => setRoteiroOpen(false)}
                className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-muted/30 p-4 sm:p-5">
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <p className="text-sm text-foreground">{selectedRoteiro?.description}</p>
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-tudo-green/5 px-3 py-2">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-tudo-green" />
                <p className="text-sm text-foreground">
                  <span className="font-semibold text-tudo-green">Objetivo:</span> {selectedRoteiro?.objetivo}
                </p>
              </div>
            </div>

            <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
              <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-2.5">
                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-tudo-blue/10">
                  <ClipboardList className="h-3.5 w-3.5 text-tudo-blue" />
                </div>
                <h3 className="text-xs font-bold uppercase tracking-wide text-tudo-blue">
                  Passo a passo
                </h3>
              </div>
              <ol className="divide-y divide-border">
                {selectedRoteiro?.steps.map((step, idx) => (
                  <li key={idx} className="flex items-start gap-3 px-4 py-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tudo-blue text-xs font-bold text-white">
                      {idx + 1}
                    </span>
                    <span className="text-sm text-foreground">{step}</span>
                  </li>
                ))}
              </ol>
            </section>
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-card px-4 py-3 sm:px-5">
            <button
              onClick={() => setRoteiroOpen(false)}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:border-tudo-blue hover:text-tudo-blue"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </button>
            <button
              onClick={() => setRoteiroOpen(false)}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-tudo-green to-tudo-green/85 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-tudo-green/20 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <CheckCircle2 className="h-4 w-4" />
              Concluir
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

